import { FileRoutesByFullPath } from '@providers';
import { UseNavigateResult } from '@tanstack/react-router';
import { DEFAULT_PAGE, getParamName } from '../../../../common';

type UseTableFilteringOptionsType = {
  navigateFn: UseNavigateResult<keyof FileRoutesByFullPath>;
  searchParams: Record<string, unknown>;
  filterKeys?: readonly string[];
  tableId?: string;
};

type UseTableFilteringReturnType = {
  filters: Record<string, string | undefined>;
  setFilter: (key: string, value: string | undefined) => void;
  setFilters: (partial: Partial<Record<string, string | undefined>>) => void;
  clearFilters: () => void;
};

export const useTableFiltering = ({
  navigateFn,
  searchParams,
  filterKeys = [],
  tableId,
}: UseTableFilteringOptionsType): UseTableFilteringReturnType => {
  const filters = Object.fromEntries(
    filterKeys.map((key) => [
      key,
      searchParams[getParamName(key, tableId)] as string | undefined,
    ])
  );

  const navigateWithFilters = (
    updates: Partial<Record<string, string | undefined>>
  ) => {
    const allowedUpdates = Object.entries(updates).filter(([key]) =>
      filterKeys.includes(key)
    );
    if (allowedUpdates.length === 0) return;

    navigateFn({
      search: (currentSearch) => {
        const nextSearch: Record<string, unknown> = { ...currentSearch };

        allowedUpdates.forEach(([key, value]) => {
          const nextValue = value && value.length > 0 ? value : undefined;
          nextSearch[getParamName(key, tableId)] = nextValue;
        });

        nextSearch[getParamName('page', tableId)] = DEFAULT_PAGE;
        return nextSearch;
      },
    });
  };

  const setFilter = (key: string, value: string | undefined) => {
    navigateWithFilters({ [key]: value });
  };

  const setFilters = (
    partial: Partial<Record<string, string | undefined>>
  ) => {
    navigateWithFilters(partial);
  };

  const clearFilters = () => {
    navigateWithFilters(
      Object.fromEntries(filterKeys.map((key) => [key, undefined]))
    );
  };

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
  };
};
